import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";

const SITE_NAME = "MindTherapy Counselling";
const SITE_URL = "https://mindtherapycounselling.com";
const DEFAULT_OG_IMAGE = `${SITE_URL}/image/header/mindtherapiyLogo.svg`;
const DEFAULT_DESCRIPTION =
  "Find your therapist and start your online therapy journey. Professional, confidential and accessible mental health support.";

const SUPPORTED_LOCALES = [
  { lang: "en", hreflang: "en" },
  { lang: "tr", hreflang: "tr" },
];

// JSON-LD: Organization schema (sitewide)
const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "MedicalOrganization",
  name: SITE_NAME,
  url: SITE_URL,
  logo: DEFAULT_OG_IMAGE,
  description: DEFAULT_DESCRIPTION,
};

// JSON-LD: WebSite schema with SearchAction (homepage only)
const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_NAME,
  url: SITE_URL,
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: `${SITE_URL}/experts?search={search_term_string}`,
    },
    "query-input": "required name=search_term_string",
  },
};

const SEO = ({
  title,
  description = DEFAULT_DESCRIPTION,
  canonical,
  ogType = "website",
  ogImage = DEFAULT_OG_IMAGE,
  noIndex = false,
  // Article schema props (for blog pages)
  articlePublishedTime,
  articleModifiedTime,
  articleAuthor,
}) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language?.split("-")[0] || "en";
  const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
  const isHomePage = canonical === `${SITE_URL}/`;
  const isArticle = ogType === "article";

  const articleSchema = isArticle && canonical
    ? {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: fullTitle,
        description,
        image: ogImage,
        url: canonical,
        ...(articlePublishedTime && { datePublished: articlePublishedTime }),
        ...(articleModifiedTime && { dateModified: articleModifiedTime }),
        ...(articleAuthor && {
          author: { "@type": "Person", name: articleAuthor },
        }),
        publisher: {
          "@type": "Organization",
          name: SITE_NAME,
          logo: { "@type": "ImageObject", url: DEFAULT_OG_IMAGE },
        },
      }
    : null;

  return (
    <Helmet>
      <html lang={currentLang} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      {canonical && <link rel="canonical" href={canonical} />}

      {/* hreflang — Çok dil desteği */}
      {canonical &&
        SUPPORTED_LOCALES.map(({ hreflang }) => (
          <link
            key={hreflang}
            rel="alternate"
            hrefLang={hreflang}
            href={canonical}
          />
        ))}
      {canonical && (
        <link rel="alternate" hrefLang="x-default" href={canonical} />
      )}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={currentLang === "tr" ? "tr_TR" : "en_US"} />
      {canonical && <meta property="og:url" content={canonical} />}
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={fullTitle} />
      {isArticle && articlePublishedTime && (
        <meta property="article:published_time" content={articlePublishedTime} />
      )}
      {isArticle && articleModifiedTime && (
        <meta property="article:modified_time" content={articleModifiedTime} />
      )}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={fullTitle} />

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      {isHomePage && (
        <script type="application/ld+json">
          {JSON.stringify(websiteSchema)}
        </script>
      )}
      {articleSchema && (
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
